import { useForm } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { MutationConfirmPreview } from "@/core/components/shared/preview-action";

import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { EntitySearchableSelect } from "@/core/components/ui/entity-searchable-select";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useCreateFeeOverride } from "@/features/merchant/api/use-create-fee-override";
import type { CreateMerchantFeeOverridePayload } from "@/features/merchant/api/dto";
import { useMerchantTranslation } from "@/features/merchant/i18n/use-merchant-translation";
import {
  createFeeOverrideSchema,
  type FeeOverrideFormValues,
} from "@/features/merchant/schemas/fee-override.schema";
import { useFeeRuleSelectOptions } from "@/features/shared/api";

export function FeeOverrideFormDialog({
  open,
  onOpenChange,
  merchantId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  merchantId: string;
}) {
  const { t } = useTypedTranslation();
  const { t: tm } = useMerchantTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : dépendance voulue.
  const schema = useMemo(() => createFeeOverrideSchema(), [i18n.language]);

  const createOverride = useCreateFeeOverride();
  const [preview, setPreview] = useState<CreateMerchantFeeOverridePayload | null>(
    null
  );
  const { options: feeRuleOptions, isLoading: feeRulesLoading } =
    useFeeRuleSelectOptions(open);

  const form = useForm({
    defaultValues: {
      merchantId,
      productCode: "",
      feeRuleId: "",
    } as FeeOverrideFormValues,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      const payload: CreateMerchantFeeOverridePayload = {
        merchantId,
        productCode: parsed.data.productCode.trim(),
        feeRuleId: parsed.data.feeRuleId.trim(),
      };
      setPreview(payload);
    },
  });

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{tm("merchant.feeOverrides.create")}</DialogTitle>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <form.Field name="productCode">
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="fee-override-product-code">
                  {tm("merchant.feeOverrides.form.productCode")}
                </Label>
                <Input
                  id="fee-override-product-code"
                  className="notranslate uppercase"
                  value={field.state.value}
                  placeholder={tm(
                    "merchant.feeOverrides.form.productCodePlaceholder"
                  )}
                  onBlur={field.handleBlur}
                  onChange={(e) =>
                    field.handleChange(e.target.value.toUpperCase())
                  }
                />
              </div>
            )}
          </form.Field>
          <form.Field name="feeRuleId">
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="fee-override-fee-rule-id">
                  {tm("merchant.feeOverrides.form.feeRuleId")}
                </Label>
                <EntitySearchableSelect
                  id="fee-override-fee-rule-id"
                  options={feeRuleOptions}
                  value={field.state.value}
                  onValueChange={(value) => field.handleChange(value)}
                  placeholder={tm(
                    "merchant.feeOverrides.form.feeRuleIdPlaceholder"
                  )}
                  isLoading={feeRulesLoading}
                />
              </div>
            )}
          </form.Field>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createOverride.isPending}
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={createOverride.isPending}>
              {createOverride.isPending ? <ButtonSpinner /> : null}
              {t("common.confirm")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
    <MutationConfirmPreview
      open={preview !== null}
      onOpenChange={(isOpen) => {
        if (!isOpen) setPreview(null);
      }}
      data={preview}
      title={t("common.mutationPreview.createTitle")}
      description={t("common.mutationPreview.createDescription")}
      t={tm}
      pending={createOverride.isPending}
      onConfirm={() => {
        if (!preview) return;
        createOverride.mutate(preview, {
          onSuccess: () => {
            notifSilence(tm("merchant.feeOverrides.feedback.created"));
            setPreview(null);
            onOpenChange(false);
          },
          onError: (error) => notifSilenceError(error.message),
        });
      }}
    />
    </>
  );
}
